/**
 * Typed API client for the MyCopy Admin API.
 *
 * Base URL is read from VITE_API_BASE_URL env var, defaulting to "/api"
 * (which Vite proxies to http://localhost:3000 during development).
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// ---------------------------------------------------------------------------
// Types — mirrors the server DTOs. Keep in sync with @mycopy/types.
// ---------------------------------------------------------------------------

export interface Machine {
  id: number;
  machineId: number;
  name: string;
  location: string | null;
  /** Форма Google-отзыва точки; QR на киоске ведёт на /g/{id}. */
  googleReviewUrl?: string | null;
  googleReviewClicks?: number;
  /** Выведен из работы из админки: на киоске экран «mimo provoz». */
  outOfService?: boolean;
  outOfServiceSince?: string | null;
  outOfServiceBy?: string | null;
  /** Киоск подтвердил в heartbeat, что экран стоит. null — ещё не сообщал. */
  outOfServiceShown?: boolean | null;
  isOnline: boolean;
  lastSeenAt: string | null;
  lowPaperThreshold: number;
  lowTonerPercent: number;
  offlineTimeoutMs: number;
  /** Стенд, а не боевая машина: её заказы не идут в выручку. */
  isSandbox?: boolean;
  /** Остаток картриджей, спрошенный у принтеров по SNMP (раз в сутки). */
  supplies?: Array<{ name: string; percent: number | null }> | null;
  suppliesMinPercent?: number | null;
  suppliesAt?: string | null;
  isArchived?: boolean;
  /** Живое состояние железа из heartbeat службы киоска (v2). */
  live?: {
    updatedAt: string;
    printerBw: string | null;
    printerColor: string | null;
    uiConnected: boolean | null;
    paperOutBw: boolean;
    paperOutColor: boolean;
    // Склад бумаги из сервис-меню (листов); null — Core старый или ещё не пополняли.
    paperBwStock?: number | null;
    paperColorStock?: number | null;
    cashboxCzk: number | null;
    tonerMinPercent: number | null;
    hopperLow: boolean;
    ups: string | null;
  } | null;
  // T044a: latest Core-app (kiosk) version reported after install/update.
  kioskVersion?: {
    version: string;
    channel: string;
    result: string; // 'ok' | 'rollback'
    reportedAt: string;
  } | null;
  peripherals?: PeripheralsReport | null;
  peripheralsUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PeripheralDevice {
  name: string;
  label: string;
  vidPid: string;
  optional: boolean;
  present: boolean;
  status: string;
}

export interface PeripheralsReport {
  devices: PeripheralDevice[];
  collectedAt: string;
}

export interface Event {
  id: string;
  machineId: number;
  type: string;
  message: string;
  details?: Record<string, unknown> | null;
  timestamp: string;
}

export interface Alert {
  id: string;
  machineId: number;
  /** Поле сервера называется alertType (колонка alert_type), не type. */
  alertType: string;
  message: string;
  severity: 'WARNING' | 'CRITICAL';
  isResolved: boolean;
  createdAt: string;
  resolvedAt: string | null;
}

// ---------------------------------------------------------------------------
// HTTP helper
// ---------------------------------------------------------------------------

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Дольше этого ни один запрос админки не ждём — иначе «Loading…» навсегда. */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Текст ошибки для человека. Тело ответа показываем только если это не HTML:
 * страница Cloudflare 524 или nginx 502 в сыром виде — простыня разметки,
 * а не сообщение (видели 14.09.2026 в Reports).
 */
function errorMessage(status: number, body: string): string {
  const trimmed = body.trim();
  if (!trimmed || /^\s*<(!doctype|html|!--)/i.test(trimmed)) {
    return status === 524 || status === 504
      ? 'Server did not answer in time (gateway timeout). Try again in a minute.'
      : `HTTP ${status}`;
  }
  try {
    const j = JSON.parse(trimmed) as { message?: unknown; error?: { message?: unknown } };
    const m = j.error?.message ?? j.message;
    if (typeof m === 'string' && m) return m;
  } catch { /* not JSON — show as is */ }
  return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: options.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      ...options,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError(0, `No answer from server in ${REQUEST_TIMEOUT_MS / 1000}s. Try again.`);
    }
    throw e;
  }

  // Session expired or missing → bounce to /login (except we're already on the
  // auth endpoints — they handle their own errors inline). 403 is a different
  // thing: logged in, but this role may not see it (e.g. operator opening the
  // cash card) — the caller decides what to show, nobody gets logged out.
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, errorMessage(res.status, body));
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, errorMessage(res.status, body));
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

/** Fetch all machines with latest status. */
export function getMachines(): Promise<Machine[]> {
  return request<Machine[]>('/machines');
}

/** Fetch a single machine by database ID. */
export function getMachine(id: number): Promise<Machine> {
  return request<Machine>(`/machines/${id}`);
}

/** Fetch events, optionally filtered by machineId and limited. */
export function getEvents(machineId?: number, limit?: number): Promise<Event[]> {
  const params = new URLSearchParams();
  if (machineId !== undefined) params.set('machineId', String(machineId));
  if (limit !== undefined) params.set('limit', String(limit));
  const qs = params.toString();
  return request<Event[]>(`/events${qs ? `?${qs}` : ''}`);
}

/** Kiosk Core debug logs (C:\MyCopy\logs\core-*.log, shipped by the Core service itself). */
export interface KioskLog {
  id: string;
  level: string;
  message: string;
  ts: string | null;
  received_at: string;
}

export function getKioskLogs(machineId: number): Promise<KioskLog[]> {
  return request<KioskLog[]>(`/agent/logs?id=${machineId}`);
}

/** Core hardware/status heartbeat (sent by the Core's AdminTelemetryService). */
export interface CoreHeartbeat {
  status: {
    machineId: number;
    uptimeSec: number;
    mock: boolean;
    online: boolean;
    printerBw: string;
    printerColor: string;
    ups: string;
    hopperLow: boolean;
    cashboxCenti: number;
    // Гроссбух кассы Core (29.10.2026): купюры / бокс монет / тубы с последней
    // инкассации; reconciled=false — касса ни разу не сверялась с sinceUtc.
    cash?: {
      billsCenti: number; coinsBoxCenti: number; coinsTestCenti: number; tubesCenti: number;
      collectCenti: number; totalCenti: number;
      sinceUtc: string | null; lastCashoutUtc: string | null;
      lastCashoutBillsCenti: number; lastCashoutCoinsCenti: number;
      reconciled: boolean;
    } | null;
    bill?: { present: boolean; configured: boolean; denoms: number[] } | null;
    coin?: { present: boolean; tubeCount: number; tubes: { czk: number; count: number }[] } | null;
    // bw/colorSheets + Capacity — уровень, прочитанный У ПРИНТЕРА по SNMP
    // (null — принтер не ответил). bw/colorUsed — старая софт-оценка от
    // последнего нажатия «Papír»: она врёт, если кнопку не нажали.
    paper?: {
      bwFull?: number; colorFull?: number; full?: number; packSheets?: number;
      // Склад из сервис-меню (листов); null/undefined — неизвестен.
      bwStock?: number | null; colorStock?: number | null;
      bwUsed: number; colorUsed: number; bwOut: boolean; colorOut: boolean;
      bwSheets?: number | null; bwCapacity?: number | null;
      colorSheets?: number | null; colorCapacity?: number | null;
    } | null;
  } | null;
  updated_at: string;
}

export function getCoreHeartbeat(machineId: number): Promise<CoreHeartbeat | null> {
  return request<CoreHeartbeat | null>(`/agent/heartbeat?id=${machineId}`);
}

/** Сколько наличных сервер ждёт в кассе по заказам с последней инкассации (T040c). */
export interface ExpectedCashbox {
  machineId: number;
  expectedCashboxCenti: number;
  expectedCashboxCzk: number;
  needsCashout: boolean;
}

export function getExpectedCashbox(machineId: number): Promise<ExpectedCashbox> {
  return request<ExpectedCashbox>(`/v1/admin/cashbox?id=${machineId}`);
}

/** Одна инкассация из журнала (admin_action{cashout}). Старые события — только amountCenti. */
export interface CashoutRecord {
  at: string;
  keyId: string | null;
  amountCenti: number;
  billsCenti: number | null;
  coinsBoxCenti: number | null;
  coinsTestCenti: number | null;
  tubesCenti: number | null;
  sinceUtc: string | null;
  reconciled: boolean | null;
}

export function getCashoutHistory(machineId: number, limit = 50): Promise<CashoutRecord[]> {
  return request<CashoutRecord[]>(`/v1/admin/cashbox/history?id=${machineId}&limit=${limit}`);
}

/** Стереть машину и всё, что на неё ссылается. Возвращает счётчики по таблицам. */
export function purgeMachine(machineId: number): Promise<{ deleted: Record<string, number> }> {
  return request<{ deleted: Record<string, number> }>(`/machines/${machineId}`, { method: 'DELETE' });
}

export interface ArchivedMachine {
  machineId: number;
  name: string;
  location: string | null;
  lastSeenAt: string | null;
  updatedAt: string;
}

/** Архив: машины, скрытые из списка, с сайта и из офлайн-алертов (superadmin). */
export function getArchivedMachines(): Promise<ArchivedMachine[]> {
  return request<ArchivedMachine[]>('/machines/archived');
}

/** Спрятать машину в архив. Обратимо, данные не трогает. */
export function archiveMachine(machineId: number): Promise<{ machineId: number; isArchived: boolean }> {
  return request(`/machines/${machineId}/archive`, { method: 'POST' });
}

/** Вывести автомат из работы (экран «mimo provoz») или вернуть. Киоск подхватит за минуту. */
export function setMachineOutOfService(machineId: number, on: boolean): Promise<Machine> {
  return request<Machine>(`/machines/${machineId}/out-of-service`, {
    method: 'POST',
    body: JSON.stringify({ on }),
  });
}

/** Вернуть машину из архива: снова в списке, на сайте и в алертах. */
export function unarchiveMachine(machineId: number): Promise<{ machineId: number; isArchived: boolean }> {
  return request(`/machines/${machineId}/unarchive`, { method: 'POST' });
}

/** Fetch all active alerts. */
export function getAlerts(): Promise<Alert[]> {
  return request<Alert[]>('/alerts');
}

/** Тестовое сообщение в Telegram-группу алертов. Ошибка = текст от Telegram. */
export function sendTelegramTest(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/alerts/telegram-test', { method: 'POST' });
}

/** Закрыть алерт руками (тонер поменяли, принтер починили). */
export function resolveAlert(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/alerts/${id}/resolve`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Pricing (per-machine + global). Backend source of truth: pricing_configs.
// ---------------------------------------------------------------------------
export interface PricingConfig {
  machineId: number | null; // null = global default
  bwPageCzk: number;
  colorPageCzk: number;
  minJobCzk: number;
  cardThresholdCzk: number;
  /** Политика наличных (14.10.2026): запас сдачи для монет, Kč; 0 = не проверять. */
  coinChangeReserveCzk?: number;
  /** Минимум монет в тубах 1 и 2 Kč, иначе монеты не принимаются; 0 = не проверять. */
  coinSmallTubeMin?: number;
  /** Самая крупная принимаемая купюра, Kč. */
  billMaxCzk?: number;
  /** Купюра только если тубы могут вернуть её целиком. */
  billRefundGuard?: boolean;
  updatedAt?: string;
}

/** List all pricing configs (global default + per-machine overrides). */
export function getPricingConfigs(): Promise<PricingConfig[]> {
  return request<PricingConfig[]>('/v1/admin/pricing');
}

/** Upsert a pricing config (machineId=null → global default). */
export function putPricing(body: PricingConfig): Promise<PricingConfig> {
  return request<PricingConfig>('/v1/admin/pricing', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Update machine fields (name, location, thresholds). */
export function updateMachine(id: number, data: Partial<Machine>): Promise<Machine> {
  return request<Machine>(`/machines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** operator — только просмотр: ни одной кнопки, которая что-то меняет. */
export type UserRole = 'superadmin' | 'admin' | 'operator';

export interface AuthUser {
  id: string;
  username: string;
  role: UserRole;
  /** Видит раздел тикетов жалоб (superadmin — всегда). */
  canHandleCases?: boolean;
}

export interface User extends AuthUser {
  canHandleCases?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Log in with username + password. Returns the authenticated user. */
export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await request<{ status: string; user: AuthUser }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return res.user;
}

/** Log out — destroys server session. */
export async function logout(): Promise<void> {
  await request<void>('/auth/logout');
}

/** Returns current authenticated user or null. */
export async function checkAuth(): Promise<AuthUser | null> {
  try {
    const res = await request<{ authenticated: boolean; user: AuthUser }>('/auth/me');
    return res.user;
  } catch {
    return null;
  }
}

/** Change own password. */
export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
  await request<void>('/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// ---------------------------------------------------------------------------
// Admin service-mode key (mycopy-admin.key) — FR-013а
// ---------------------------------------------------------------------------

export interface AdminKey {
  keyId: string;
  passwordHash: string;
}

/** Current admin key {keyId, passwordHash}. 404 if none set yet. */
export function getAdminKeyFile(): Promise<AdminKey> {
  return request<AdminKey>('/v1/admin/key-file');
}

/** Set a new admin service-mode password → bumps keyId, revokes the old key. */
export function rotateAdminPassword(password: string): Promise<{ keyId: string }> {
  return request<{ keyId: string }>('/v1/admin/key-file/rotate', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/** Superadmin: list all users. */
export function listUsers(): Promise<User[]> {
  return request<User[]>('/users');
}

/** Superadmin: create a new user. */
export function createUser(data: { username: string; password: string; role?: UserRole }): Promise<User> {
  return request<User>('/users', { method: 'POST', body: JSON.stringify(data) });
}

/** Superadmin: reset a user's password. */
export function setUserPassword(id: string, password: string): Promise<void> {
  return request<void>(`/users/${id}/password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/** Superadmin: delete a user. */
export function deleteUser(id: string): Promise<void> {
  return request<void>(`/users/${id}`, { method: 'DELETE' });
}

/** Fetch server and agent versions. */
export interface VersionInfo {
  server: string;
}

export function getVersion(): Promise<VersionInfo> {
  return request<VersionInfo>('/version');
}

// ---------------------------------------------------------------------------
// Manual refund queue (FR-026 / US6)
// ---------------------------------------------------------------------------

export interface RefundItem {
  sessionId: string;
  code: string | null;
  state: string;
  machineId: number | null;
  createdTerminal: string;
  method: 'online' | 'cash' | null;
  owedCzk: number;
  paidAt: string | null;
  createdAt: string;
}

/** Admin: sessions awaiting a manual refund. */
export function getRefundQueue(): Promise<RefundItem[]> {
  return request<RefundItem[]>('/v1/admin/refunds');
}

/** Admin: mark a session as refunded (operator did the refund by hand). */
export function completeRefund(
  sessionId: string,
): Promise<{ ok: boolean; sessionId: string; amountCzk: number }> {
  return request(`/v1/admin/refunds/${sessionId}/complete`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Sessions — lifecycle view (GET /api/admin/sessions[/:code])
// ---------------------------------------------------------------------------

export interface SessionListItem {
  code: string | null;
  id: string;
  kind: string;
  state: string;
  origin: string;
  machineId: number | null;
  createdTerminal: string;
  priceCents: number | null;
  paymentMethod: string | null;
  paidAt: string | null;
  refunded: boolean;
  createdAt: string;
  lastEventType: string | null;
  lastEventAt: string | null;
  lastTerminal: string | null;
}

export interface SessionEventRow {
  seq: number;
  type: string;
  actor: string;
  terminal: string;
  payload: unknown;
  occurredAt: string;
  recordedAt: string;
}

export interface SessionTrace {
  session: SessionListItem & {
    email?: string | null;
    refundReason?: string | null;
    printMode?: unknown;
    expiresAt?: string | null;
  };
  events: SessionEventRow[];
}

/** Admin: recent sessions (newest first), each with its latest stage. */
export function getSessions(opts: {
  limit?: number;
  machineId?: number;
  state?: string;
  kind?: string;
} = {}): Promise<SessionListItem[]> {
  const p = new URLSearchParams();
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.machineId != null) p.set('machineId', String(opts.machineId));
  if (opts.state) p.set('state', opts.state);
  if (opts.kind) p.set('kind', opts.kind);
  const qs = p.toString();
  return request<SessionListItem[]>(`/admin/sessions${qs ? `?${qs}` : ''}`);
}

/** Admin: one session + its full event timeline. */
export function getSessionTrace(code: string): Promise<SessionTrace> {
  return request<SessionTrace>(`/admin/sessions/${encodeURIComponent(code)}`);
}

/** Every state a session can be put into by hand (mirrors the DB enum). */
export const SESSION_STATES = [
  'created', 'uploading', 'selecting', 'awaiting_payment', 'paid', 'code_issued',
  'printing', 'completed', 'cancelled', 'expired', 'failed', 'refunded',
] as const;
export type SessionStateName = (typeof SESSION_STATES)[number];

/**
 * Admin: set a session's state by hand (superadmin only).
 *
 * Support tool — a drifted state makes the kiosk refuse a paid code, and this
 * puts the customer back on track without touching the database. Journalled as
 * `state_overridden`.
 */
export function setSessionState(
  code: string,
  state: SessionStateName,
  reason?: string,
): Promise<{ code: string; from: string; to: string }> {
  return request(`/admin/sessions/${encodeURIComponent(code)}/state`, {
    method: 'PATCH',
    body: JSON.stringify({ state, reason }),
  });
}

// ── Отчёты ─────────────────────────────────────────────────────────────────

export interface ReportRow {
  code: string | null;
  machineId: number | null;
  machineName: string | null;
  kind: string;
  color: string | null;
  pages: number | null;
  sheets: number | null;
  priceCzk: number;
  paymentMethod: string;
  refunded: boolean;
  createdAt: string;
}

export interface ReportTotals {
  orders: number;
  revenueCzk: number;
  sheets: number;
  byMethod: Record<string, { orders: number; revenueCzk: number }>;
  byMachine: Array<{ machineId: number | null; machineName: string | null; orders: number; revenueCzk: number }>;
}

function reportQuery(o: { from: string; to: string; machineIds?: number[]; includeSandbox?: boolean }) {
  const p = new URLSearchParams({ from: o.from, to: o.to });
  if (o.machineIds?.length) p.set('machineIds', o.machineIds.join(','));
  if (o.includeSandbox) p.set('includeSandbox', '1');
  return p;
}

/** Отчёт за период: строки + итоги. Песочница исключена, если не попросить. */
export function getReport(o: {
  from: string; to: string; machineIds?: number[]; includeSandbox?: boolean;
}): Promise<{ rows: ReportRow[]; totals: ReportTotals }> {
  return request(`/admin/reports/sessions?${reportQuery(o).toString()}`);
}

/** Тот же отчёт в CSV — скачивается как файл (Excel открывает без плясок). */
export async function downloadReportCsv(o: {
  from: string; to: string; machineIds?: number[]; includeSandbox?: boolean;
}): Promise<void> {
  const qs = reportQuery(o);
  qs.set('format', 'csv');
  const res = await fetch(`${BASE_URL}/admin/reports/sessions?${qs.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => `HTTP ${res.status}`));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mycopy-${o.from}-${o.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface RevenueSummary {
  today: { orders: number; revenueCzk: number };
  week: { orders: number; revenueCzk: number };
  month: { orders: number; revenueCzk: number };
  byMachineMonth: Array<{ machineId: number | null; machineName: string | null; orders: number; revenueCzk: number }>;
  /** Последние 30 дней (пражский календарь), старые сначала, пустые дни — нули. */
  daily?: Array<{ day: string; orders: number; revenueCzk: number }>;
  /** Рекордный день за всё время; null — заказов не было. */
  bestDay?: { day: string; orders: number; revenueCzk: number } | null;
  allTime?: { orders: number; revenueCzk: number };
}

/** Сводка выручки для главной. Считается тем же правилом, что и отчёт. */
export function getRevenueSummary(): Promise<RevenueSummary> {
  return request('/admin/reports/summary');
}

/** Занятость пространства 8-значных кодов заказов. Только superadmin (иначе 403). */
export interface CodeSpace {
  used: number;
  space: number;
  usedPct: number;
  last30Days: number;
  yearsToFivePct: number | null;
}
export function getCodeSpace(): Promise<CodeSpace> {
  return request('/admin/reports/code-space');
}

// ── Отзывы покупателей (экран «Hotovo» на киоске) ──────────────────────────

export type FeedbackRating = 'good' | 'ok' | 'bad';

export interface FeedbackTotals { good: number; ok: number; bad: number; total: number }

export interface FeedbackSummary {
  days: number;
  totals: FeedbackTotals;
  byMachine: Array<FeedbackTotals & { machineId: number; machineName: string | null; lastBadAt: string | null }>;
  badReasons: Array<{ reason: string | null; count: number }>;
}

export interface FeedbackReview {
  id: string;
  machineId: number;
  machineName: string | null;
  rating: FeedbackRating;
  reason: string | null;
  code: string | null;
  source: string | null;
  at: string;
}

/** days=0 — за всё время. */
export function getFeedbackSummary(days = 30): Promise<FeedbackSummary> {
  return request(`/admin/feedback/summary?days=${days}`);
}

export function getFeedback(o: {
  days?: number; machineId?: number; rating?: FeedbackRating; limit?: number;
} = {}): Promise<FeedbackReview[]> {
  const qs = new URLSearchParams();
  qs.set('days', String(o.days ?? 30));
  if (o.machineId) qs.set('machineId', String(o.machineId));
  if (o.rating) qs.set('rating', o.rating);
  if (o.limit) qs.set('limit', String(o.limit));
  return request(`/admin/feedback?${qs.toString()}`);
}

// ── Тикеты жалоб (docs/architecture/order-complaints.md) ─────────────────────

export type CaseStatus = 'new' | 'in_progress' | 'waiting_customer' | 'waiting_bank_account' | 'to_pay' | 'resolved' | 'rejected';

export interface CaseRow {
  id: string;
  number: number;
  status: CaseStatus;
  category: string;
  code: string | null;
  machineId: number | null;
  machineName: string | null;
  createdAt: string;
  updatedAt: string;
  resolution: string | null;
  refundCzk: number | null;
  assigneeId: string | null;
  assignee: string | null;
  reportedPaidCzk: number | null;
  description: string | null;
  photos: number;
}

export interface CaseDetail extends Omit<CaseRow, 'photos'> {
  kioskReason: string | null;
  reportedMachineId: number | null;
  reportedAt: string | null;
  reportedPayment: string | null;
  email: string | null;
  lang: string;
  bankAccount: string | null;
  resolutionNote: string | null;
  rootCause: string | null;
  source: string;
  resolvedAt: string | null;
  resolvedByName: string | null;
  purgedAt: string | null;
  sessionId: string | null;
  photos: Array<{ id: string; width: number | null; height: number | null }>;
  events: Array<{ id: string; type: string; actor: string; actorName: string | null; payload: Record<string, unknown> | null; createdAt: string }>;
  session: {
    id: string; code: string | null; state: string; priceCzk: number | null; paymentMethod: string | null;
    paid: boolean; paidAt: string | null; createdAt: string; machineId: number | null; refunded: boolean | null;
    hasPaymentIntent: boolean; printMode: Record<string, unknown> | null;
  } | null;
  trail: Array<{ type: string; actor: string; terminal: string; at: string; payload: Record<string, unknown> | null }>;
  nearby: {
    cases?: Array<{ id: string; number: number; category: string; createdAt: string }>;
    alerts?: Array<{ type: string; message: string; createdAt: string; resolvedAt: string | null }>;
    events?: Array<{ type: string; details: Record<string, unknown> | null; at: string }>;
  };
  mailConfigured: boolean;
  caseUrl: string;
}

export interface CaseStats {
  days: number; total: number; open: number; refundedCzk: number; medianHoursToResolve: number | null;
  byCategory: Array<{ key: string; count: number }>;
  byRootCause: Array<{ key: string; count: number }>;
  byResolution: Array<{ key: string; count: number }>;
  byMachine: Array<{ key: string; count: number }>;
}

export function listCases(o: { status?: string; machineId?: number; mine?: boolean } = {}): Promise<CaseRow[]> {
  const qs = new URLSearchParams();
  if (o.status) qs.set('status', o.status);
  if (o.machineId) qs.set('machineId', String(o.machineId));
  if (o.mine) qs.set('mine', '1');
  return request(`/admin/cases?${qs.toString()}`);
}
export const getCaseStats = (days = 30) => request<CaseStats>(`/admin/cases/stats?days=${days}`);
export const getCaseDetail = (id: string) => request<CaseDetail>(`/admin/cases/${id}`);
export const casePhotoUrl = (id: string, photoId: string) => `${BASE_URL}/admin/cases/${id}/photos/${photoId}`;
const casePost = (id: string, action: string, body?: unknown) =>
  request<CaseDetail>(`/admin/cases/${id}/${action}`, { method: 'POST', body: JSON.stringify(body ?? {}) });
export const assignCase = (id: string, assigneeId?: string | null) => casePost(id, 'assign', { assigneeId });

/** Reply templates — shared by all operators, inserted into the email text by a button. */
export interface CaseTemplate { id: string; title: string; body: string; createdBy: string | null; createdAt: string; updatedAt: string }
export const listCaseTemplates = () => request<CaseTemplate[]>('/admin/case-templates');
export const createCaseTemplate = (title: string, body: string) =>
  request<CaseTemplate>('/admin/case-templates', { method: 'POST', body: JSON.stringify({ title, body }) });
export const updateCaseTemplate = (id: string, title: string, body: string) =>
  request<CaseTemplate>(`/admin/case-templates/${id}`, { method: 'PUT', body: JSON.stringify({ title, body }) });
export const deleteCaseTemplate = (id: string) => request<{ ok: true }>(`/admin/case-templates/${id}`, { method: 'DELETE' });
export const messageCase = (id: string, text: string, askCustomer: boolean) => casePost(id, 'message', { text, askCustomer });
export const refundCaseCard = (id: string, amountCzk: number, note: string, rootCause: string) => casePost(id, 'refund-card', { amountCzk, note, rootCause });
export const requestCaseBank = (id: string, amountCzk: number, note: string, rootCause: string) => casePost(id, 'request-bank-account', { amountCzk, note, rootCause });
export const revealCaseBank = (id: string) => request<{ bankAccount: string; refundCzk: number | null; number: number }>(`/admin/cases/${id}/reveal-bank-account`, { method: 'POST', body: '{}' });
export const markCasePaid = (id: string) => casePost(id, 'mark-paid');
export const resolveCase = (id: string, resolution: string, note: string, rootCause: string) => casePost(id, 'resolve', { resolution, note, rootCause });
export const rejectCase = (id: string, note: string, rootCause: string) => casePost(id, 'reject', { note, rootCause });
export const reopenCase = (id: string) => casePost(id, 'reopen');

/** Галочка доступа к тикетам (superadmin ставит на странице Users). */
export function setUserCasesAccess(id: string, enabled: boolean): Promise<void> {
  return request<void>(`/users/${id}/cases-access`, { method: 'POST', body: JSON.stringify({ enabled }) });
}
